import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { elapsed, label } from "./format";
import { heartbeatStale, runPhase } from "./runPhase";
import type { MonitorRun } from "./types";

export function QueueRows({
  items,
  asOf,
  onSelect,
}: {
  items: MonitorRun[];
  asOf: string;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="divide-y rounded-lg border">
      {items.map((run) => {
        const phase = runPhase(run);
        const awaitingUser =
          run.lifecycle === "active" && run.wait_counts.user_input;
        return (
          <div
            key={run.workload_id}
            className="grid gap-3 p-4 sm:grid-cols-[minmax(0,3fr)_minmax(0,2fr)_minmax(0,1fr)]"
          >
            <div className="min-w-0">
              <p className="font-medium">
                {awaitingUser ? "Awaiting user" : phase.label}
              </p>
              {(phase.detail || awaitingUser) && (
                <p className="text-sm text-muted-foreground">
                  {awaitingUser
                    ? `${phase.label}${phase.detail ? ` · ${phase.detail}` : ""}`
                    : phase.detail}
                </p>
              )}
              <Button
                data-queue-workload={run.workload_id}
                variant="link"
                className="mt-1 h-auto max-w-full justify-start truncate p-0 font-mono text-xs text-muted-foreground"
                onClick={() => onSelect(run.workload_id)}
              >
                {run.workload_id}
              </Button>
            </div>
            <div className="flex flex-wrap content-start gap-1">
              <Badge variant="secondary">
                {label(run.product_flow)} · {label(run.channel)}
                {run.run_kind !== "echo" ? ` · ${run.run_kind}` : ""}
              </Badge>
              {run.lifecycle !== "active" && (
                <Badge
                  variant={
                    run.lifecycle === "failed_final" ? "destructive" : "outline"
                  }
                >
                  {label(run.lifecycle)}
                </Badge>
              )}
              {run.attention.map((code) => (
                <Badge key={code} variant="destructive">
                  {label(code)}
                </Badge>
              ))}
              {heartbeatStale(run) && (
                <Badge variant="outline">Heartbeat stale</Badge>
              )}
              <span className="w-full text-xs text-muted-foreground">
                Last activity {elapsed(run.last_observed_at, asOf)} ago ·{" "}
                {label(run.evidence_quality)}
              </span>
            </div>
            <div className="text-sm sm:text-right">
              <p>
                {elapsed(
                  run.admitted_at ?? run.first_observed_at,
                  run.terminal_at ?? asOf,
                )}
              </p>
              <p className="text-xs text-muted-foreground">
                {run.lifecycle !== "active"
                  ? "Admission to exit"
                  : run.admitted_at
                    ? "Since admission"
                    : "Since first observed"}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
